import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CampaignThroughputData, FarmingOperationCommercialGrainClass, farmingOperationCommercialGrainSelectedPayload, PercentageOfThroughput } from '../../types/assessment_info/gev/BusinessOperationsData';
import { OperationFieldProps } from '../../types/assessment_info/gev/BusinessOperationFormProps';
import { useBusinessOperationsState } from '../../utils/context/BusinessOperationsContext';
import { useFormErrorContext } from '../../utils/context/FormErrorContext';
import { useReadOnly } from '../../utils/ReadOnlyContext';
import { farmingOption } from '../../pages/BusinessOperations/BusinessOperations';
import AIRPNumberInput from '../common/AIRPNumberInput/AIRPNumberInput';
import "./GEV.css";

function isFarmingOperation(dataset: FarmingOperationCommercialGrainClass) {
    return dataset &&
        typeof dataset.selected === 'boolean' &&
        'percentageOfThroughput' in dataset
}

export default function GEVPercentageOfThroughput(props: OperationFieldProps): JSX.Element {
    const { t } = useTranslation('gev')
    const { formData, setFormData } = useBusinessOperationsState();
    const { isReadOnly } = useReadOnly()
    const colKeys = ["yearValue", "owner", "nonOwner", "totalThroughput"];
    const { errors } = useFormErrorContext();
    const [errorFields, setErrorFields] = useState({});

    const initPOTData = () => {
        let pot: PercentageOfThroughput | undefined = undefined
        if (isFarmingOperation(formData[farmingOption])) {
            pot = formData[farmingOption].percentageOfThroughput
        }
        else {
            pot = structuredClone(farmingOperationCommercialGrainSelectedPayload.percentageOfThroughput)
        }
        const activeCampaignYear = Number(sessionStorage.getItem("activeCampaignYear"))
        if (pot && activeCampaignYear) {
            pot.campaignYearOne.yearValue = activeCampaignYear - 3
            pot.campaignYearTwo.yearValue = activeCampaignYear - 2
            pot.campaignYearThree.yearValue = activeCampaignYear - 1
        }
        return pot
    }
    const [potData, setPotData] = useState<PercentageOfThroughput | undefined>(() => initPOTData())

    const handlePotChange = (value: number | null, yearKey: keyof PercentageOfThroughput, yearObj: CampaignThroughputData) => {
        props.removeError && props.removeError(yearKey)

        let ownerValue = null;
        if (value) {
            const numberValue = Number(value)
            ownerValue = numberValue
        }

        const newData: CampaignThroughputData = {
            yearValue: yearObj.yearValue,
            owner: ownerValue,
            nonOwner: ownerValue === null ? null : (100 - ownerValue),
            totalThroughput: ownerValue === null ? null : 100
        }

        setPotData(prevData => {
            const dataset = prevData as PercentageOfThroughput;
            return {
                ...dataset,
                [yearKey]: newData
            }
        });
    }

    useEffect(() => {
        setErrorFields({});
        if (errors.length > 0) {
            errors.forEach((error) => {
                if (error.primaryKey && (error.primaryKey.split('.')[2] === props.name || error.primaryKey.split('.')[1] === props.name)) {
                    const splitKey = error.primaryKey.split('.');
                    const localKey = splitKey.slice(-2).join(".");
                    setErrorFields(prevData => ({ ...prevData, [localKey]: error.message }));
                }
            })
        }
    }, [errors, props.name]);

    useEffect(() => {
        setFormData((prevData) => {
            const dataset = prevData.farmingOperationCommercialGrain;
            dataset.percentageOfThroughput = potData;
            return {
                ...prevData,
                [props.operation]: dataset
            }
        });
    }, [potData, props.operation, setFormData])

    return (
        <tr className='gev-input-tr'>
            <td className='index'>
                {props.index && props.index + "."}
            </td>
            <td>
                <div className='gev-input-label'>{t(props.operation + "." + props.name + ".label")}</div>
                <table width={"100%"} className='percentageOfThroughput' role="none">
                    <tbody>
                        <tr key={"heading"}>
                            {
                                colKeys.map((colKey, index) => {
                                    if (index === 0) {
                                        return <td key={colKey} width={"10%"}>{t(props.operation + "." + props.name + "." + colKey)}</td>
                                    } else if (index === 1) {
                                        return <td key={colKey} width={"50%"}>{t(props.operation + "." + props.name + "." + colKey)}</td>
                                    } else {
                                        return <td key={colKey} width={"20%"}>{t(props.operation + "." + props.name + "." + colKey)}</td>
                                    }
                                })
                            }
                        </tr>
                        {potData && Object.entries(potData as PercentageOfThroughput).map(([yearKey, yearObj]) => {
                            return (
                                <tr key={yearKey} id={`${props.operation}.${props.name}.${yearKey}`}>
                                    <td key={yearObj.yearValue}>{yearObj.yearValue}</td>
                                    <td key={`${yearKey}-owner`}>
                                        <div className='gev-pot'>
                                            <div className='gev-pot-number-input-div'>
                                                <AIRPNumberInput
                                                    formatType="decimal"
                                                    name={`${props.operation}.${props.name}.${yearKey}.owner`}
                                                    initialValue={yearObj.owner ?? ""}
                                                    readOnly={isReadOnly}
                                                    onChange={(value: number | null) => handlePotChange(value, yearKey as keyof PercentageOfThroughput, yearObj)}
                                                    className={`gev-number-input-100 ${(`${yearKey}.owner` in errorFields) ? 'form-error' : ''}`}
                                                />
                                            </div>
                                            <div>%</div>
                                        </div>
                                    </td>
                                    <td key={`${props.operation}.${props.name}.${yearKey}.nonOwner`}>{yearObj.nonOwner && (yearObj.nonOwner as number).toFixed(2)} %</td>
                                    <td key={`${props.operation}.${props.name}.${yearKey}.totalThroughput`}>{yearObj.totalThroughput} %</td>
                                </tr>
                            )
                        })
                        }
                    </tbody>
                </table>
            </td>
        </tr>
    )
}
